/**
 * CSS var() Resolver
 *
 * Recursively resolves CSS custom property references (`var(--x, fallback)`)
 * to concrete values using getComputedStyle(). Necessary because `var()`
 * references are not valid across iframe boundaries.
 *
 * Handles nested var() references, fallbacks, and cycle detection.
 */

export const MAX_CSS_VAR_RESOLVE_DEPTH = 12

export function findMatchingParen(value: string, openParenIndex: number): number {
	let depth = 1
	for (let i = openParenIndex + 1; i < value.length; i++) {
		const char = value[i]
		if (char === '(') depth += 1
		else if (char === ')') depth -= 1
		if (depth === 0) return i
	}
	return -1
}

export function splitTopLevelComma(value: string): [string, string | undefined] {
	let depth = 0
	for (let i = 0; i < value.length; i++) {
		const char = value[i]
		if (char === '(') depth += 1
		else if (char === ')') depth = Math.max(0, depth - 1)
		else if (char === ',' && depth === 0) return [value.slice(0, i), value.slice(i + 1)]
	}
	return [value, undefined]
}

export function resolveFallback(
	fallback: string | undefined,
	computed: CSSStyleDeclaration,
	seen: Set<string>,
	depth: number,
): string | undefined {
	if (!fallback) return undefined
	return resolveCssValue(fallback, computed, seen, depth + 1) || undefined
}

export function resolveCssVariable(
	variableName: string,
	fallback: string | undefined,
	computed: CSSStyleDeclaration,
	seen: Set<string>,
	depth: number,
): string | undefined {
	if (depth > MAX_CSS_VAR_RESOLVE_DEPTH) {
		console.warn(`[Char] CSS variable resolution depth exceeded for "${variableName}". Possible circular reference.`)
		return fallback?.trim()
	}

	if (!variableName.startsWith('--') || seen.has(variableName)) {
		return resolveFallback(fallback, computed, seen, depth)
	}

	const nextSeen = new Set(seen)
	nextSeen.add(variableName)

	const rawValue = computed.getPropertyValue(variableName).trim()
	if (rawValue) {
		const resolvedRaw = resolveCssValue(rawValue, computed, nextSeen, depth + 1)
		if (resolvedRaw) return resolvedRaw
	}

	return resolveFallback(fallback, computed, seen, depth)
}

export function resolveCssValue(
	value: string,
	computed: CSSStyleDeclaration,
	seen: Set<string>,
	depth = 0,
): string {
	const trimmed = value.trim()
	if (!trimmed || !trimmed.includes('var(') || depth > MAX_CSS_VAR_RESOLVE_DEPTH) {
		return trimmed
	}

	let cursor = 0
	let resolved = ''
	let replacedAny = false

	while (cursor < trimmed.length) {
		const varStart = trimmed.indexOf('var(', cursor)
		if (varStart === -1) {
			resolved += trimmed.slice(cursor)
			break
		}

		resolved += trimmed.slice(cursor, varStart)

		const openParenIndex = varStart + 3
		const closeParenIndex = findMatchingParen(trimmed, openParenIndex)
		if (closeParenIndex === -1) {
			resolved += trimmed.slice(varStart)
			break
		}

		const rawArgs = trimmed.slice(openParenIndex + 1, closeParenIndex)
		const [rawVariableName, rawFallback] = splitTopLevelComma(rawArgs)
		const variableName = rawVariableName.trim()
		const fallback = rawFallback?.trim()

		const replacement = variableName
			? resolveCssVariable(variableName, fallback, computed, seen, depth + 1)
			: undefined

		if (replacement !== undefined) {
			resolved += replacement
			replacedAny = true
		} else {
			resolved += trimmed.slice(varStart, closeParenIndex + 1)
		}

		cursor = closeParenIndex + 1
	}

	const normalized = resolved.trim()
	if (!replacedAny || !normalized.includes('var(')) {
		return normalized
	}

	return resolveCssValue(normalized, computed, seen, depth + 1)
}
